'use client'

import { ReactElement, useEffect, useRef, useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'

import clsx from 'clsx'

import { SETTINGS_GENERAL } from '@/config/settings.general'
import WaitListModal from '@/containers/site/WaitListModal'
import { useReachGoal } from '@/hooks/useReachGoal'

import Banner from './components/Banner'
import Footer from './components/Footer'
import logo from './icons/logo.svg'
import text from './icons/text.svg'
import cloudL from './images/cloud-l.webp'
import cloudM from './images/cloud-m.webp'
import cloudS from './images/cloud-s.webp'
import cloudXl from './images/cloud-xl.webp'
import cloudXs from './images/cloud-xs.webp'
import gift from './images/gift.webp'
import styles from './styles.module.scss'

const clouds = [
    {
        image: cloudXl,
        className: clsx(styles.cloud, styles.cloud__xl),
    },
    {
        image: cloudL,
        className: clsx(styles.cloud, styles.cloud__l),
    },
    {
        image: cloudM,
        className: clsx(styles.cloud, styles.cloud__m),
    },
    {
        image: cloudS,
        className: clsx(styles.cloud, styles.cloud__s1),
    },
    {
        image: cloudS,
        className: clsx(styles.cloud, styles.cloud__s2),
    },
    {
        image: cloudXs,
        className: clsx(styles.cloud, styles.cloud__xs1),
    },
    {
        image: cloudXs,
        className: clsx(styles.cloud, styles.cloud__xs2),
    },
]

const SURVEY_GOOGLE_DOCS_LINK =
    'https://docs.google.com/forms/d/e/1FAIpQLSfolzjmkwTJDA8gQT18uRvCmLXfOSesoWMerR5Cj9LCWNhBAw/viewform?usp=preview'

export default function Survey(): ReactElement {
    const [isWaitListModal, setIsWaitListModal] = useState(false)

    const refClouds = useRef<HTMLDivElement>(null)

    const reachGoal = useReachGoal()

    useEffect(() => {
        if (refClouds.current) {
            const container = refClouds.current
            const clouds = Array.from(
                container.querySelectorAll<HTMLDivElement>(`.${styles.cloud}`)
            )
            if (!clouds.length) return
            const states = clouds.map((el, index) => ({
                el,
                x: 0,
                y: 0,
                direction: Math.random() < 0.5 ? 1 : -1,
                speed: 10 + Math.random() * 20,
                index,
            }))

            const initCloud = (
                state: (typeof states)[number],
                spread = false
            ): void => {
                const containerWidth = container.offsetWidth || 1
                const containerHeight = container.offsetHeight || 1
                const el = state.el

                const cloudHeight = el.offsetHeight || containerHeight * 0.2
                const maxTop = Math.max(0, containerHeight * 0.5 - cloudHeight)
                const top = Math.random() * maxTop
                state.y = top
                el.style.top = `${top}px`

                const cloudWidth = el.offsetWidth || cloudHeight * 2

                if (spread) {
                    state.x =
                        (Math.random() * 1.4 - 0.2) * containerWidth -
                        cloudWidth / 2
                    const center = containerWidth / 2
                    state.direction = state.x + cloudWidth / 2 < center ? 1 : -1
                    state.speed = 10 + Math.random() * 20
                } else {
                    state.direction = Math.random() < 0.5 ? 1 : -1
                    state.speed = 10 + Math.random() * 20
                    if (state.direction > 0) {
                        state.x =
                            -cloudWidth - Math.random() * (containerWidth * 0.3)
                    } else {
                        state.x =
                            containerWidth +
                            Math.random() * (containerWidth * 0.3)
                    }
                }

                el.style.transform = `translate3d(${state.x}px, 0, 0)`
            }

            requestAnimationFrame(() => {
                states.forEach(s => initCloud(s, true))
            })

            let lastTime = performance.now()

            const loop = (now: number): void => {
                const dt = (now - lastTime) / 1000
                lastTime = now
                const containerWidth = container.offsetWidth || 1
                states.forEach(state => {
                    const el = state.el
                    const cloudWidth = el.offsetWidth || 0
                    state.x += state.direction * state.speed * dt
                    if (
                        state.direction > 0 &&
                        state.x > containerWidth + cloudWidth
                    ) {
                        initCloud(state, false)
                    } else if (state.direction < 0 && state.x < -cloudWidth) {
                        initCloud(state, false)
                    } else {
                        el.style.transform = `translate3d(${state.x}px, 0, 0)`
                    }
                })
                requestAnimationFrame(loop)
            }

            requestAnimationFrame(t => {
                lastTime = t
                requestAnimationFrame(loop)
            })
        }
    }, [])

    return (
        <>
            <main className={styles.wrapper}>
                <div className={styles.outer}>
                    <div className={styles.background}>
                        <div className={styles.background__gradient} />
                        <div
                            ref={refClouds}
                            className={styles.background__clouds}
                        >
                            {clouds.map((cloud, index) => (
                                <Image
                                    key={`cloud-${index}`}
                                    src={cloud.image}
                                    alt={`Облако ${index}`}
                                    className={cloud.className}
                                    loading={'eager'}
                                    draggable={false}
                                />
                            ))}
                        </div>
                    </div>
                    <div className={styles.inner}>
                        <div className={styles.content}>
                            <Link
                                href={SETTINGS_GENERAL.SITE_URL}
                                className={styles.brand}
                                onClick={() =>
                                    reachGoal({
                                        goalName: 'went-to-the-main-site',
                                    })
                                }
                            >
                                <Image
                                    src={logo}
                                    alt={'BukaGo'}
                                    className={styles.brand__image}
                                    draggable={false}
                                />
                                <div className={styles.brand__block}>
                                    <Image
                                        src={text}
                                        alt={'BukaGo'}
                                        className={styles.brand__text}
                                        draggable={false}
                                    />
                                    <div className={styles.brand__description}>
                                        {'Родительский контроль и геолокация'}
                                    </div>
                                </div>
                            </Link>
                            <h1 className={styles.title}>
                                {'Пройди опрос и получи '}
                                <span className={styles.title__accent}>
                                    {'полгода пользования бесплатно'}
                                </span>
                            </h1>
                            <p className={styles.description}>
                                {
                                    'Делись с другими мамами опросом, если они укажут твою почту при заполнении, то подарим на аккаунт еще 1 месяц плюсом за каждого человека.'
                                }
                            </p>
                            <div className={styles.buttons}>
                                <Link
                                    href={SURVEY_GOOGLE_DOCS_LINK}
                                    target={'_blank'}
                                    className={styles.button}
                                    onClick={() =>
                                        reachGoal({
                                            goalName: 'click-take-the-survey',
                                        })
                                    }
                                >
                                    {'Пройти опрос'}
                                </Link>
                                {/*<Link*/}
                                {/*    href={''}*/}
                                {/*    target={'_blank'}*/}
                                {/*    className={styles.link}*/}
                                {/*>*/}
                                {/*    {'Голосовать в Telegram'}*/}
                                {/*</Link>*/}
                            </div>
                        </div>
                        <div className={styles.image}>
                            <Image
                                src={gift}
                                alt={'BukaGo'}
                                draggable={false}
                            />
                        </div>
                    </div>
                </div>
            </main>
            <Banner onOpenWaitListModal={() => setIsWaitListModal(true)} />
            <Footer />
            <WaitListModal
                isVisible={isWaitListModal}
                onClose={() => setIsWaitListModal(false)}
            />
        </>
    )
}
